import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Heartbeat, NotePencil, FileText, Paperclip, User as UserIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PatientRecordPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    anamnesis: '',
    blood_group: '',
    allergies: '',
    chronic_conditions: '',
    medications: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
  });

  const [newNote, setNewNote] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const fetchRecord = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/me/patient-record');
      setRecord(r.data);
      setForm({
        anamnesis: r.data.anamnesis || '',
        blood_group: r.data.blood_group || '',
        allergies: (r.data.allergies || []).join(', '),
        chronic_conditions: (r.data.chronic_conditions || []).join(', '),
        medications: r.data.medications || '',
        emergency_contact_name: r.data.emergency_contact_name || '',
        emergency_contact_phone: r.data.emergency_contact_phone || '',
        notes: r.data.notes || '',
      });
    } catch { toast.error('Errore caricamento cartella'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchRecord();
  }, [user, authLoading, navigate, fetchRecord]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        allergies: form.allergies.split(',').map((s) => s.trim()).filter(Boolean),
        chronic_conditions: form.chronic_conditions.split(',').map((s) => s.trim()).filter(Boolean),
      };
      await api.put('/api/me/patient-record', payload);
      toast.success('Cartella aggiornata');
      fetchRecord();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return toast.error('Inserisci il testo della nota');
    try {
      await api.post('/api/me/patient-record/notes', { body: newNote.trim(), visit_date: visitDate || null });
      setNewNote(''); setVisitDate('');
      toast.success('Nota aggiunta');
      fetchRecord();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File max 10MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'patient_record');
      const r = await api.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post('/api/me/patient-record/attachments', {
        file_id: r.data.file_id,
        label: file.name,
      });
      toast.success('Allegato caricato');
      fetchRecord();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm('Rimuovere questo allegato?')) return;
    try {
      await api.delete(`/api/me/patient-record/attachments/${attId}`);
      toast.success('Allegato rimosso');
      fetchRecord();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">Caricamento cartella…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="patient-record-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs mb-3">
            <Heartbeat size={14} weight="duotone" /> Cartella Assistenziale
          </div>
          <h1 className="font-heading font-semibold text-4xl mb-2">La tua cartella</h1>
          <p className="text-foreground/70">
            Anamnesi, note delle visite e allegati. Visibile solo a te, ai professionisti attualmente in booking attivo e allo staff VivaLink.
          </p>
        </div>

        {/* Anamnesis */}
        <Card className="neumorphism rounded-2xl p-6 mb-6" data-testid="anamnesis-card">
          <div className="flex items-center gap-2 mb-4">
            <NotePencil size={20} weight="duotone" className="text-primary" />
            <h2 className="font-semibold text-xl">Anamnesi</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Anamnesi generale</Label>
              <Textarea rows={4} value={form.anamnesis} onChange={(e) => setForm({ ...form, anamnesis: e.target.value })} data-testid="anamnesis-input" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Gruppo sanguigno</Label>
                <Input value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} placeholder="Es. 0+" data-testid="blood-input" />
              </div>
              <div>
                <Label>Farmaci in uso</Label>
                <Input value={form.medications} onChange={(e) => setForm({ ...form, medications: e.target.value })} data-testid="medications-input" />
              </div>
            </div>
            <div>
              <Label>Allergie (separate da virgola)</Label>
              <Input value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} placeholder="Es. penicillina, arachidi" data-testid="allergies-input" />
            </div>
            <div>
              <Label>Patologie croniche (separate da virgola)</Label>
              <Input value={form.chronic_conditions} onChange={(e) => setForm({ ...form, chronic_conditions: e.target.value })} data-testid="chronic-input" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Contatto d&apos;emergenza — Nome</Label>
                <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} data-testid="emergency-name-input" />
              </div>
              <div>
                <Label>Contatto d&apos;emergenza — Telefono</Label>
                <Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} data-testid="emergency-phone-input" />
              </div>
            </div>
            <div>
              <Label>Note aggiuntive</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="notes-input" />
            </div>
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="rounded-full" data-testid="save-record-btn">
                {saving ? 'Salvataggio…' : 'Salva cartella'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Visit notes */}
        <Card className="neumorphism rounded-2xl p-6 mb-6" data-testid="visit-notes-card">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} weight="duotone" className="text-primary" />
            <h2 className="font-semibold text-xl">Note delle visite ({record?.visit_notes?.length || 0})</h2>
          </div>

          <div className="mb-6 space-y-2 p-4 rounded-xl bg-muted/40">
            <Label className="text-xs">Aggiungi una nota</Label>
            <Textarea rows={2} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Descrivi cosa è successo nella visita…" data-testid="new-note-input" />
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Data visita (opzionale)</Label>
                <Input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} data-testid="visit-date-input" />
              </div>
              <Button onClick={addNote} className="rounded-full" data-testid="add-note-btn">Aggiungi</Button>
            </div>
          </div>

          {(!record?.visit_notes || record.visit_notes.length === 0) ? (
            <p className="text-sm text-foreground/60 text-center py-6">Nessuna nota ancora.</p>
          ) : (
            <div className="space-y-3">
              {record.visit_notes.map((n) => (
                <div key={n.note_id} className="border border-border rounded-xl p-4" data-testid={`note-${n.note_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <UserIcon size={14} className="text-primary" weight="duotone" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">{n.author_name}</div>
                        <Badge variant="outline" className="text-[10px]">{n.author_role}</Badge>
                      </div>
                    </div>
                    <div className="text-xs text-foreground/60 text-right">
                      {n.visit_date && <div>Visita: {new Date(n.visit_date).toLocaleDateString('it-IT')}</div>}
                      <div>{new Date(n.created_at).toLocaleString('it-IT')}</div>
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{n.body}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Attachments */}
        <Card className="neumorphism rounded-2xl p-6" data-testid="attachments-card">
          <div className="flex items-center gap-2 mb-4">
            <Paperclip size={20} weight="duotone" className="text-primary" />
            <h2 className="font-semibold text-xl">Allegati ({record?.attachments?.length || 0})</h2>
          </div>

          <div className="mb-6 p-4 rounded-xl bg-muted/40">
            <p className="text-sm text-foreground/70 mb-3">
              Carica referti, ricette, esami e altri documenti (max 10MB — PDF, immagini, doc).
            </p>
            <input
              ref={fileInputRef}
              type="file"
              onChange={uploadAttachment}
              className="hidden"
              accept="image/*,application/pdf,.doc,.docx"
              data-testid="attachment-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-full"
              data-testid="upload-attachment-btn"
            >
              {uploading ? 'Caricamento…' : '+ Carica documento'}
            </Button>
          </div>

          {(!record?.attachments || record.attachments.length === 0) ? (
            <p className="text-sm text-foreground/60 text-center py-6">Nessun allegato ancora.</p>
          ) : (
            <ul className="space-y-2">
              {record.attachments.map((a) => (
                <li key={a.attachment_id} className="flex items-center justify-between p-3 border border-border rounded-lg" data-testid={`att-${a.attachment_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.label || a.file_id}</div>
                    <div className="text-xs text-foreground/60">{new Date(a.created_at).toLocaleString('it-IT')}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => removeAttachment(a.attachment_id)} data-testid={`remove-att-${a.attachment_id}`}>
                      Rimuovi
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PatientRecordPage;
